import { ref, watch, markRaw, type Component } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import axios from '@/axios'
import MetaFilter, { type FilterRange } from '@/filters/meta'
import type {
  DBFilterOption,
  DBFilterValue,
  FilterOption,
  FilterValue,
  Filter
} from '@/filters/meta'
import { FilterType } from '@/filters/meta'
import type { Asset } from '@/location'
import { useLocationStore } from '@/store/location'

import TtItemToggle from '@/components/TtItemToggle.vue'
import TtSlider from '@/components/TtSlider.vue'
import TtItemToggleAccordion from '@/components/TtItemToggleAccordion.vue'

import _ from 'lodash'

import { i18n } from '@/i18n'
const { t } = i18n.global

const instance = axios.create({
  baseURL: import.meta.env.VITE_GATEWAY_URL + '/ui'
})

const validComponents = new Map(
  Object.entries({
    TtItemToggle: markRaw(TtItemToggle),
    TtSlider: markRaw(TtSlider),
    TtItemToggleAccordion: markRaw(TtItemToggleAccordion)
  })
) as Map<string, Component>

const getFilterValue = (filter: DBFilterValue): FilterValue => {
  if (!filter) return {} as FilterValue
  const v = {
    field: filter.field,
    key: filter.key,
    value: filter.value
  } as FilterValue

  switch (filter.type) {
    case 'bool':
      v.value = filter.value === 'true'
      break
    case 'text':
      v.value = filter.value
      break
    case 'number':
      if (filter.value) v.value = +filter.value
      break
    case 'range':
      v.value = filter.range as FilterRange
      break
  }

  return v
}

const jsonSerializer = {
  read: (v: any) => (v ? JSON.parse(v) : null),
  write: (v: any) => JSON.stringify(v)
}

export const useMetaFilterStore = defineStore('metafilter', () => {
  const locationStore = useLocationStore()
  const { aggregation } = storeToRefs(locationStore)

  const activeIcons = ref<Map<string, Asset[][]>>(new Map())
  const filter = ref<MetaFilter>()
  const total = ref(0)
  const rawSections = ref<any[]>([])

  watch(
    () => aggregation.value,
    (agg) => {
      total.value = agg.total
      filter.value?.setAggregations(agg.aggregates)
      activeIcons.value = filter.value?.getIcons() ?? new Map()
    },
    { immediate: true, deep: true }
  )

  function translateAndBuild(sections: any[]) {
    const convertFilterOption = (option: DBFilterOption): FilterOption => {
      const translatedOption = t(option.label) // TODO: Translate this in the backend instead
      const translatedChildren = {} as { [key: string]: FilterOption }

      for (const child of (option?.children ?? []) as DBFilterOption[]) {
        translatedChildren[child.key] = convertFilterOption(child)
      }

      return {
        ...option,
        label: translatedOption,
        filter: getFilterValue(option.filter),
        component: validComponents.get(option.component),
        props: {
          number: 0,
          ...option.props
        },
        children: translatedChildren
      } as FilterOption
    }

    // iterate over each section and translate the labels
    const filters = sections.map((section: { label: string; options: unknown[] }) => {
      const { label, options } = section
      const translatedLabel = t(label) // TODO: Translate this in the backend instead
      const translatedOptions = {} as { [key: string]: FilterOption }

      for (const option of options as DBFilterOption[]) {
        translatedOptions[option.key] = convertFilterOption(option)
      }

      return {
        ...section,
        label: translatedLabel,
        options: translatedOptions
      }
    })

    // Preserve active state from the existing filter before replacing it
    if (filter.value) {
      const preserveActive = (options: Filter) => {
        for (const key in options) {
          const oldOption = filter.value!.getOption(key)
          if (oldOption) {
            options[key].active = oldOption.active
            if (oldOption.type === FilterType.Range) {
              options[key].filter.value = oldOption.filter.value
            }
            if (options[key].children) {
              preserveActive(options[key].children as Filter)
            }
          }
        }
      }
      for (const section of filters) {
        preserveActive(section.options)
      }
    }

    filter.value = new MetaFilter(filters)
  }

  watch(
    () => i18n.global.locale.value,
    () => {
      if (rawSections.value.length > 0) {
        translateAndBuild(rawSections.value)
      }
    }
  )

  return {
    total,
    filter,
    activeIcons,
    buildFilter(): void {
      instance
        .get('/metafilter')
        .then((response) => {
          rawSections.value = response?.data?.sections ?? []
          translateAndBuild(rawSections.value)
        })
        .catch((error) => {
          console.error(error)
        })
    }
  }
})
